import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalContext } from '../App';
import CCDatasetService from '../services/CCDatasetService';
import { CC_SELECTION_KEY, countryForRegion } from '../components/crop-intelligence/contract';
import CCV2Provider, { readCCIdentity } from '../components/crop-intelligence/CCV2Provider';

const CCContext = createContext(null);
export const useCC = () => useContext(CCContext);
export const isCCPage = (pathname) => pathname === '/cc' || pathname === '/cc/';
export const supportsCCOverlay = (pathname) => pathname === '/' || pathname === '/home' || pathname.startsWith('/home/');

function readSelection() {
  try { return JSON.parse(localStorage.getItem(CC_SELECTION_KEY)) || {}; } catch (error) { return {}; }
}

export function CCProvider({ children, initialTransport = 'v2' }) {
  const [transport, setTransport] = useState(initialTransport);
  useEffect(() => {
    if (transport !== 'legacy') return;
    const identity = readCCIdentity();
    const inspect = () => { if (readCCIdentity() !== identity) setTransport('v2'); };
    const timer = setInterval(inspect, 1000);
    window.addEventListener('storage', inspect); window.addEventListener('focus', inspect);
    return () => { clearInterval(timer); window.removeEventListener('storage', inspect); window.removeEventListener('focus', inspect); };
  }, [transport]);
  return transport === 'legacy'
    ? <LegacyCCProvider setTransport={setTransport}>{children}</LegacyCCProvider>
    : <CCV2Provider setTransport={setTransport}>{children}</CCV2Provider>;
}

export function LegacyCCProvider({ children, setTransport }) {
  const { pathname } = useLocation();
  const { globalCCViewEnabled, globalFieldDrawUIEnabled, globalSelectedRegion } = useContext(GlobalContext);
  const active = isCCPage(pathname) || (supportsCCOverlay(pathname) && globalCCViewEnabled && !globalFieldDrawUIEnabled);
  const country = countryForRegion(globalSelectedRegion);
  const [selection, setSelection] = useState(readSelection);
  const [result, setResult] = useState({ status: 'idle', country: null, metadata: null });
  const [revision, setRevision] = useState(0);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const generation = useRef(0);
  const retry = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    if (!active || country !== 'TR') return;
    const controller = new AbortController();
    const requestId = ++generation.current;
    setResult({ status: 'loading', country, metadata: null });
    CCDatasetService.getDatasets(country, controller.signal).then((metadata) => {
      if (controller.signal.aborted || requestId !== generation.current) return;
      setResult({ status: 'ready', country, metadata });
      setSelection((previous) => {
        if (previous.country === country && previous.id) return previous;
        const dataset = metadata.datasets.find((item) => item.year === metadata.default_year);
        return dataset ? { country, id: dataset.id, year: dataset.year } : {};
      });
    }).catch((error) => {
      if (controller.signal.aborted || requestId !== generation.current) return;
      console.error('CC metadata request failed', error);
      setResult({ status: 'error', country, metadata: null });
    });
    return () => { controller.abort(); generation.current += 1; };
  }, [active, country, revision]);

  useEffect(() => {
    if (!selection.id) return;
    try { localStorage.setItem(CC_SELECTION_KEY, JSON.stringify(selection)); } catch (error) { /* Storage may be unavailable. */ }
  }, [selection]);

  const metadata = result.country === country && result.status === 'ready' ? result.metadata : null;
  const dataset = metadata?.datasets.find((item) => selection.country === country && item.id === selection.id && item.year === selection.year) || null;
  let status = country !== 'TR' ? 'unsupported' : result.country === country ? result.status : 'loading';
  if (metadata && !metadata.datasets.length) status = 'unavailable';
  else if (metadata && !dataset) status = 'selectionUnavailable';
  const selectDataset = useCallback((id) => {
    const next = metadata?.datasets.find((item) => item.id === id);
    if (next) {
      setSelectedPolygon(null);
      setSelection({ country, id: next.id, year: next.year });
    }
  }, [metadata, country]);

  useEffect(() => { setSelectedPolygon(null); }, [dataset?.id, active, globalSelectedRegion]);
  const visiblePolygon = active && dataset && selectedPolygon?.properties?.dataset_id === dataset.id ? selectedPolygon : null;

  return <CCContext.Provider value={{ transport: 'legacy', setTransport, active, country, metadata, dataset: active ? dataset : null,
    status, selection, selectDataset, retry, selectedPolygon: visiblePolygon, setSelectedPolygon }}>
    {children}
  </CCContext.Provider>;
}

export default CCContext;
